/**
 * Automatic captions generated in the browser. While the studio is live on the swarm and captions
 * are on, the Web Speech API (`SpeechRecognition` / `webkitSpeechRecognition`) transcribes the
 * microphone continuously and the cues are sent to the control plane once a second, authenticated
 * with the stream key like the heartbeat. Nothing is transcribed on a server.
 *
 * The Web Speech API cannot be handed a MediaStream: it always listens to the browser's default
 * microphone, whatever the mixer is doing. See docs/architecture.md, "Live captions".
 */
import { CAPTION_TEXT_MAX, type StreamingSettings } from '../core/model';
import type { Logger, Unsubscribe } from '../core/services';
import type { SwarmState } from '../net/api';
import { createStore, errorMessage } from '../net/store';

const SCOPE = 'engine.captions';

/** Cues are sent at most this often. */
export const CAPTION_FLUSH_MS = 1000;
/** At most this many cues per request. */
export const CAPTION_BATCH_MAX = 50;
/** Unsent cues beyond this are dropped, oldest first (a long outage must not grow without bound). */
export const CAPTION_QUEUE_MAX = 500;
/** Delay before restarting recognition after it ended normally (Chrome stops after silence). */
export const CAPTION_RESTART_MS = 250;
export const CAPTION_RESTART_MAX_MS = 10_000;
/** A recognition run that ends sooner than this without a result counts as a failure for the backoff. */
export const CAPTION_QUICK_END_MS = 1000;
/** After this many failed runs in a row the status turns to 'error' (recognition keeps retrying). */
const FAILURES_BEFORE_ERROR = 3;

export type CaptionStatus = 'idle' | 'listening' | 'unsupported' | 'error';

export interface CaptionEngineState {
  status: CaptionStatus;
  /** The latest recognised text (interim or final); '' when nothing yet. */
  lastText: string;
  /** Why captions are unsupported or failing; null otherwise. */
  error: string | null;
}

/**
 * One caption line. `seq` identifies an utterance: it is resent while the text is interim and once
 * more with `final: true`. `startMs`/`endMs` are milliseconds since captions started for this
 * broadcast; `capturedAt` is the wall clock when the cue was produced (comparable with the
 * segments' capturedAt).
 */
export interface CaptionCue {
  seq: number;
  capturedAt: number;
  startMs: number;
  endMs: number;
  text: string;
  lang: string;
  final: boolean;
}

export interface CaptionTarget {
  apiUrl: string;
  streamKey: string;
  streamId: string;
  lang: string;
}

/**
 * Where captions go right now: only while live on the swarm (reconnects included) with captions
 * on; null means the engine must stop.
 */
export function captionTargetFor(streaming: StreamingSettings, swarm: Pick<SwarmState, 'status' | 'streamId'>): CaptionTarget | null {
  const live = streaming.mode === 'swarm' && (swarm.status === 'live' || swarm.status === 'reconnecting');
  if (!live || !swarm.streamId || !streaming.captions.enabled) return null;
  return { apiUrl: streaming.swarmApiUrl.trim(), streamKey: streaming.swarmStreamKey.trim(), streamId: swarm.streamId, lang: streaming.captions.lang };
}

/** The part of the Web Speech API this engine uses. */
export interface SpeechRecognitionLike {
  continuous: boolean;
  interimResults: boolean;
  lang: string;
  maxAlternatives: number;
  onstart: ((ev: unknown) => void) | null;
  onresult: ((ev: SpeechResultEventLike) => void) | null;
  onerror: ((ev: { error: string; message?: string }) => void) | null;
  onend: ((ev: unknown) => void) | null;
  start(): void;
  stop(): void;
  abort(): void;
}

export interface SpeechResultEventLike {
  resultIndex: number;
  results: ArrayLike<{ readonly isFinal: boolean; readonly length: number; readonly [index: number]: { transcript: string } | undefined }>;
}

export type SpeechRecognitionCtor = new () => SpeechRecognitionLike;

export interface CaptionSupport {
  ctor: SpeechRecognitionCtor | null;
  /** Why captions cannot run in this browser; null when they can. */
  reason: string | null;
}

const UNSUPPORTED_REASON = 'This browser has no speech recognition. Automatic captions need Chrome or Edge.';
const SAFARI_REASON = "Safari's speech recognition stops after each phrase, so automatic captions need Chrome or Edge.";

/** Chrome and Edge expose `webkitSpeechRecognition`; Firefox has none; Safari's is not continuous. */
export function detectCaptionSupport(scope: object = globalThis): CaptionSupport {
  const g = scope as { SpeechRecognition?: unknown; webkitSpeechRecognition?: unknown; navigator?: { vendor?: string } };
  const ctor = (g.SpeechRecognition ?? g.webkitSpeechRecognition) as SpeechRecognitionCtor | undefined;
  if (typeof ctor !== 'function') return { ctor: null, reason: UNSUPPORTED_REASON };
  if (g.navigator?.vendor === 'Apple Computer, Inc.') return { ctor: null, reason: SAFARI_REASON };
  return { ctor, reason: null };
}

/** Errors after which restarting cannot help. */
const FATAL_ERRORS: Record<string, string> = {
  'not-allowed': 'The browser denied microphone access for speech recognition.',
  'service-not-allowed': 'The browser does not allow its speech recognition service here.',
  'language-not-supported': 'The browser cannot recognise speech in the chosen language.',
};

export interface CaptionEngineDeps {
  logger: Logger;
  fetch?: typeof fetch;
  /** Defaults to detectCaptionSupport(). */
  support?: CaptionSupport;
  /** A toast for the operator. */
  notify?: (level: 'info' | 'warn' | 'error', message: string) => void;
}

export interface CaptionEngine {
  getState(): CaptionEngineState;
  subscribe(listener: (state: CaptionEngineState) => void): Unsubscribe;
  /**
   * Starts (or retargets) recognition and sending. Calling it again with the same target does
   * nothing; a new language for the same stream keeps the cue numbering and time origin.
   */
  start(target: CaptionTarget): void;
  /** Stops recognition, drops unsent cues and returns to idle (or unsupported). The next start numbers cues from 1 again. */
  stop(): void;
}

interface Utterance {
  seq: number;
  startMs: number;
  text: string;
  final: boolean;
}

/** Numbering and the time origin of one broadcast's captions; kept when only the language changes. */
interface Session {
  streamId: string;
  startedAt: number;
  nextSeq: number;
}

interface Run {
  target: CaptionTarget;
  base: string;
  session: Session;
  queue: CaptionCue[];
  sending: boolean;
  flushTimer: ReturnType<typeof setInterval>;
  restartTimer: ReturnType<typeof setTimeout> | null;
  abort: AbortController;
  recognition: SpeechRecognitionLike | null;
  /** Results of the current recognition object, by result index (indexes restart with each object). */
  utterances: Map<number, Utterance>;
  recognitionStartedAt: number;
  gotResult: boolean;
  failures: number;
  lastError: string | null;
  fatal: boolean;
  refusedNotified: boolean;
}

const sameTarget = (a: CaptionTarget, b: CaptionTarget): boolean =>
  a.apiUrl === b.apiUrl && a.streamKey === b.streamKey && a.streamId === b.streamId && a.lang === b.lang;

const cleanTranscript = (text: unknown): string => (typeof text === 'string' ? text.replace(/\s+/g, ' ').trim().slice(0, CAPTION_TEXT_MAX) : '');

export function createCaptionEngine(deps: CaptionEngineDeps): CaptionEngine {
  const { logger } = deps;
  const doFetch: typeof fetch = deps.fetch ?? ((input, init) => fetch(input, init));
  const support = deps.support ?? detectCaptionSupport();
  const baseline = (): CaptionEngineState =>
    support.ctor ? { status: 'idle', lastText: '', error: null } : { status: 'unsupported', lastText: '', error: support.reason };
  const store = createStore<CaptionEngineState>(baseline());
  let run: Run | null = null;
  let session: Session | null = null;

  function enqueue(r: Run, cue: CaptionCue): void {
    // An unsent interim of the same utterance is superseded: only its newest text matters.
    const index = r.queue.findIndex((queued) => queued.seq === cue.seq);
    if (index >= 0) r.queue[index] = cue;
    else r.queue.push(cue);
    if (r.queue.length > CAPTION_QUEUE_MAX) r.queue.splice(0, r.queue.length - CAPTION_QUEUE_MAX);
  }

  async function flush(r: Run): Promise<void> {
    if (run !== r || r.sending || r.queue.length === 0) return;
    const cues = r.queue.splice(0, CAPTION_BATCH_MAX);
    r.sending = true;
    try {
      const res = await doFetch(`${r.base}/api/streams/${encodeURIComponent(r.target.streamId)}/captions`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${r.target.streamKey.trim()}` },
        body: JSON.stringify({ lang: r.target.lang, cues }),
        credentials: 'omit',
        cache: 'no-store',
        signal: r.abort.signal,
      });
      if (run !== r || res.ok) return;
      if (res.status === 409 || res.status === 403 || res.status === 401) {
        logger.log('warn', SCOPE, `ompeer.com refused ${cues.length} caption cue(s) (${res.status})`);
        if (!r.refusedNotified) {
          r.refusedNotified = true;
          deps.notify?.(
            'warn',
            res.status === 401
              ? 'ompeer.com refused the captions: the stream key is not valid (401).'
              : res.status === 403
                ? 'ompeer.com refused the captions: the stream key does not match this stream (403).'
                : 'ompeer.com refused the captions: the stream is not live there (409). Captions are dropped until it is.',
          );
        }
        return;
      }
      logger.log('debug', SCOPE, `sending captions answered ${res.status}; ${cues.length} cue(s) dropped`);
    } catch (err) {
      if (run === r) logger.log('debug', SCOPE, `sending captions failed: ${errorMessage(err)}`);
    } finally {
      r.sending = false;
    }
  }

  function onResult(r: Run, rec: SpeechRecognitionLike, ev: SpeechResultEventLike): void {
    if (run !== r || r.recognition !== rec) return;
    r.gotResult = true;
    r.failures = 0;
    r.lastError = null;
    for (let i = ev.resultIndex; i < ev.results.length; i++) {
      const result = ev.results[i];
      if (!result) continue;
      const text = cleanTranscript(result[0]?.transcript);
      let u = r.utterances.get(i);
      if (!u) {
        if (!text) continue;
        u = { seq: r.session.nextSeq++, startMs: Math.max(0, Date.now() - r.session.startedAt), text: '', final: false };
        r.utterances.set(i, u);
      }
      if (u.final) continue;
      const final = result.isFinal === true;
      if (!final && (text === u.text || !text)) continue;
      if (text) u.text = text;
      u.final = final;
      if (!u.text) continue;
      const now = Date.now();
      enqueue(r, { seq: u.seq, capturedAt: now, startMs: u.startMs, endMs: Math.max(u.startMs, now - r.session.startedAt), text: u.text, lang: r.target.lang, final });
      store.set({ lastText: u.text });
    }
    store.set({ status: 'listening', error: null });
  }

  function onEnd(r: Run, rec: SpeechRecognitionLike): void {
    if (run !== r || r.recognition !== rec) return;
    r.recognition = null;
    if (r.fatal) return;
    const quick = !r.gotResult && Date.now() - r.recognitionStartedAt < CAPTION_QUICK_END_MS;
    r.failures = quick ? r.failures + 1 : 0;
    if (r.failures >= FAILURES_BEFORE_ERROR) {
      store.set({ status: 'error', error: r.lastError ?? 'Speech recognition keeps stopping; retrying.' });
    }
    const delay = r.failures === 0 ? CAPTION_RESTART_MS : Math.min(CAPTION_RESTART_MAX_MS, CAPTION_RESTART_MS * 2 ** r.failures);
    r.restartTimer = setTimeout(() => {
      r.restartTimer = null;
      listen(r);
    }, delay);
  }

  function onError(r: Run, rec: SpeechRecognitionLike, ev: { error: string; message?: string }): void {
    if (run !== r || r.recognition !== rec) return;
    const fatal = FATAL_ERRORS[ev.error];
    if (fatal) {
      r.fatal = true;
      logger.log('warn', SCOPE, `speech recognition failed: ${ev.error}`);
      store.set({ status: 'error', error: fatal });
      deps.notify?.('warn', `Automatic captions stopped: ${fatal}`);
      return;
    }
    // 'no-speech', 'aborted', 'network', 'audio-capture': the end event follows and restarts.
    r.lastError = `Speech recognition error: ${ev.error}.`;
    logger.log('debug', SCOPE, `speech recognition error: ${ev.error}`);
  }

  function listen(r: Run): void {
    if (run !== r || r.fatal || !support.ctor) return;
    const rec = new support.ctor();
    rec.continuous = true;
    rec.interimResults = true;
    rec.lang = r.target.lang;
    rec.maxAlternatives = 1;
    rec.onresult = (ev) => onResult(r, rec, ev);
    rec.onerror = (ev) => onError(r, rec, ev);
    rec.onend = () => onEnd(r, rec);
    r.recognition = rec;
    r.utterances = new Map();
    r.gotResult = false;
    r.recognitionStartedAt = Date.now();
    try {
      rec.start();
      if (r.failures < FAILURES_BEFORE_ERROR) store.set({ status: 'listening', error: null });
    } catch (err) {
      r.lastError = `Speech recognition did not start: ${errorMessage(err)}`;
      logger.log('debug', SCOPE, r.lastError);
      onEnd(r, rec);
    }
  }

  function teardown(r: Run): void {
    clearInterval(r.flushTimer);
    if (r.restartTimer) clearTimeout(r.restartTimer);
    r.abort.abort();
    const rec = r.recognition;
    r.recognition = null;
    if (rec) {
      rec.onresult = rec.onerror = rec.onend = null;
      try {
        rec.abort();
      } catch {
        // Already stopped.
      }
    }
  }

  return {
    getState: store.get,
    subscribe: store.subscribe,
    start(target) {
      if (run && sameTarget(run.target, target)) return;
      // No notification until the new run is installed: a listener may call start() again.
      const previous = run;
      run = null;
      if (previous) teardown(previous);
      if (!support.ctor) {
        store.set(baseline());
        return;
      }
      if (session?.streamId !== target.streamId) session = { streamId: target.streamId, startedAt: Date.now(), nextSeq: 1 };
      const r: Run = {
        target: { ...target },
        base: target.apiUrl.trim().replace(/\/+$/, ''),
        session,
        queue: [],
        sending: false,
        flushTimer: setInterval(() => void flush(r), CAPTION_FLUSH_MS),
        restartTimer: null,
        abort: new AbortController(),
        recognition: null,
        utterances: new Map(),
        recognitionStartedAt: 0,
        gotResult: false,
        failures: 0,
        lastError: null,
        fatal: false,
        refusedNotified: false,
      };
      run = r;
      store.set({ status: 'idle', lastText: '', error: null });
      listen(r);
    },
    stop() {
      session = null;
      const r = run;
      run = null;
      if (r) teardown(r);
      store.set(baseline());
    },
  };
}
